import React, { useState, useMemo } from "react";
import { Icon } from "@iconify/react";
import ReactApexChart from "react-apexcharts";

const FREQ_STYLE = {
  "Ghosting":          { bg: "#fef2f2", color: "#dc2626", dot: "#ef4444" },
  "Low Activity":      { bg: "#fef9c3", color: "#92400e", dot: "#f59e0b" },
  "Moderately Active": { bg: "#f0fdf4", color: "#15803d", dot: "#22c55e" },
  "Highly Active":     { bg: "#f0fdf4", color: "#15803d", dot: "#16a34a" },
  "Active":            { bg: "#f0fdf4", color: "#15803d", dot: "#22c55e" },
  "Dormant":           { bg: "#fef2f2", color: "#dc2626", dot: "#ef4444" },
};

const PALETTE = ["#6366f1","#22c55e","#3b82f6","#f59e0b","#06b6d4","#8b5cf6","#ef4444","#10b981","#0ea5e9","#f97316"];
const colorFor = (key = "") => {
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  return PALETTE[h % PALETTE.length];
};
const initialsOf = (name = "") => {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  return ((parts[0][0] || "") + (parts[1]?.[0] || "")).toUpperCase();
};
const sumTimesheetSeconds = (timesheet) => {
  if (!timesheet || typeof timesheet !== "object") return 0;
  return Object.values(timesheet).reduce((a, v) => a + (Number(v) || 0), 0);
};
const formatDuration = (seconds) => {
  if (!seconds || seconds <= 0) return "0m";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return m ? `${h}h ${m}m` : `${h}h`;
  if (m > 0) return `${m}m`;
  return `${seconds}s`;
};
const formatDateTime = (d) => {
  if (!d) return null;
  const dt = new Date(d);
  if (isNaN(dt)) return null;
  return dt.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
};
const daysBetween = (from, to = new Date()) => {
  if (!from) return Infinity;
  const dt = new Date(from);
  if (isNaN(dt)) return Infinity;
  return Math.floor((to - dt) / 86400000);
};
const frequencyLabel = (loginDayCount) => {
  if (loginDayCount >= 26) return "Highly Active";
  if (loginDayCount >= 15) return "Moderately Active";
  if (loginDayCount >= 6)  return "Low Activity";
  return "Ghosting";
};

const SENTIMENT_FROM_EMOJI = {
  "very_happy": "Very Happy", "very-happy": "Very Happy", "veryhappy": "Very Happy", "love": "Very Happy", "heart_eyes": "Very Happy", "🤩": "Very Happy", "😍": "Very Happy",
  "happy": "Happy", "smile": "Happy", "smiley": "Happy", "grinning": "Happy", "🙂": "Happy", "😊": "Happy", "😀": "Happy", "😃": "Happy",
  "neutral": "Neutral", "meh": "Neutral", "neutral_face": "Neutral", "😐": "Neutral", "😑": "Neutral",
  "not_happy": "Not Happy", "sad": "Not Happy", "angry": "Not Happy", "frown": "Not Happy", "frowning": "Not Happy", "☹️": "Not Happy", "😞": "Not Happy", "😡": "Not Happy", "😠": "Not Happy",
};
const sentimentFor = (emoji) => {
  if (!emoji) return "Neutral";
  const key = String(emoji).toLowerCase().trim();
  return SENTIMENT_FROM_EMOJI[key] || SENTIMENT_FROM_EMOJI[emoji] || "Neutral";
};
const RATING_FROM_SENTIMENT = { "Very Happy": 5, "Happy": 4, "Neutral": 3, "Not Happy": 1 };

const SubAccountDetail = ({ account, users = [], loginActivity = [], feedback = [], onBack }) => {
  const [activeTab, setActiveTab] = useState("loginActivity");
  const [trendMetric, setTrendMetric] = useState("timeSpent");
  const [trendRange, setTrendRange] = useState("30D");
  const [userSearch, setUserSearch] = useState("");

  const accountId = account?.id;
  const accountRaw = account?.raw || {};

  /* Users that belong to this sub-account */
  const accountUsers = useMemo(() => {
    return users.filter((u) =>
      Array.isArray(u.ghl_locations) && u.ghl_locations.includes(accountId)
    );
  }, [users, accountId]);

  /* Login activity scoped to this sub-account */
  const accountActivity = useMemo(() => {
    return loginActivity.filter((la) => String(la.LocationId) === String(accountId));
  }, [loginActivity, accountId]);

  /* Per-user stats (within this account) */
  const userStats = useMemo(() => {
    const map = new Map();
    const since = Date.now() - 30 * 86400000;
    accountActivity.forEach((la) => {
      const uid = la.userId ? String(la.userId) : null;
      if (!uid) return;
      const ts = la.createdAt ? new Date(la.createdAt).getTime() : 0;
      const cur = map.get(uid) || { lastLogin: null, loginDays: new Set() };
      if (!cur.lastLogin || ts > new Date(cur.lastLogin).getTime()) cur.lastLogin = la.createdAt;
      if (ts >= since) {
        const d = new Date(ts);
        cur.loginDays.add(`${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`);
      }
      map.set(uid, cur);
    });
    return map;
  }, [accountActivity]);

  /* Top stat cards */
  const statCards = useMemo(() => {
    const since30 = Date.now() - 30 * 86400000;
    const activeUserIds = new Set(
      accountActivity
        .filter((la) => new Date(la.createdAt).getTime() >= since30)
        .map((la) => String(la.userId))
        .filter(Boolean)
    );
    const lastLoginTs = accountActivity.reduce((max, la) => {
      const ts = la.createdAt ? new Date(la.createdAt).getTime() : 0;
      return ts > max ? ts : max;
    }, 0);
    return [
      { label: "ACTIVE USERS",     value: String(activeUserIds.size), sub: "Last 30 days" },
      { label: "TOTAL TIME SPENT", value: formatDuration(account?.totalSeconds ?? sumTimesheetSeconds(accountRaw.timesheet)), sub: "All time" },
      { label: "LAST LOGIN TIME",  value: lastLoginTs ? formatDateTime(new Date(lastLoginTs)) : "Never", sub: lastLoginTs ? `${daysBetween(lastLoginTs)}d ago` : "-" },
    ];
  }, [accountActivity, account, accountRaw]);

  const userRows = useMemo(() => {
    return accountUsers.map((u) => {
      const totalSeconds = sumTimesheetSeconds(u.timesheet);
      const stats = userStats.get(String(u.id));
      const fullName = `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.username || u.email || "Unnamed";
      const freq = stats ? frequencyLabel(stats.loginDays.size) : "Ghosting";
      return {
        id: u.id,
        name: fullName,
        email: u.email || "—",
        initials: initialsOf(fullName),
        color: colorFor(u.id || fullName),
        role: (u.ghl_role || u.type || "User").replace(/^./, (c) => c.toUpperCase()),
        lastLogin: formatDateTime(stats?.lastLogin),
        timeSpent: totalSeconds > 0 ? formatDuration(totalSeconds) : null,
        freq,
      };
    });
  }, [accountUsers, userStats]);

  const filteredUserRows = useMemo(() =>
    userRows.filter((u) =>
      u.name.toLowerCase().includes(userSearch.toLowerCase()) ||
      u.email.toLowerCase().includes(userSearch.toLowerCase())
    ),
  [userRows, userSearch]);

  /* Trend data (per-day for selected range) */
  const { trendCats, trendVals } = useMemo(() => {
    const days = trendRange === "7D" ? 7 : trendRange === "30D" ? 30 : 90;
    const now = new Date();
    const cats = [];
    const buckets = new Map();
    for (let i = days - 1; i >= 0; i--) {
      const d = new Date(now);
      d.setDate(now.getDate() - i);
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      const label = d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
      cats.push(label);
      buckets.set(key, { idx: cats.length - 1, count: 0 });
    }
    accountActivity.forEach((la) => {
      if (!la.createdAt) return;
      const d = new Date(la.createdAt);
      if (isNaN(d)) return;
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      if (buckets.has(key)) buckets.get(key).count += 1;
    });
    const vals = Array(days).fill(0);
    buckets.forEach((b) => { vals[b.idx] = b.count; });
    return { trendCats: cats, trendVals: vals };
  }, [accountActivity, trendRange]);

  /* ── Time Spent breakdowns ─────────────────────────────────────── */
  // Feature breakdown — from the LOCATION's timesheet (canonical aggregate)
  const featureBreakdown = useMemo(() => {
    const ts = accountRaw.timesheet || {};
    const entries = Object.entries(ts)
      .filter(([k, v]) => k && Number(v) > 0)
      .map(([feature, seconds]) => ({ feature, seconds: Number(seconds) || 0 }))
      .sort((a, b) => b.seconds - a.seconds);
    const total = entries.reduce((a, e) => a + e.seconds, 0);
    return { entries, total };
  }, [accountRaw]);

  // Per-user breakdown — sum each user's own timesheet, plus their per-feature map
  const userTimeBreakdown = useMemo(() => {
    const rows = accountUsers.map((u) => {
      const ts = u.timesheet || {};
      const features = Object.entries(ts)
        .filter(([k, v]) => k && Number(v) > 0)
        .map(([feature, seconds]) => ({ feature, seconds: Number(seconds) || 0 }))
        .sort((a, b) => b.seconds - a.seconds);
      const total = features.reduce((a, f) => a + f.seconds, 0);
      const fullName = `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.username || u.email || "Unnamed";
      return {
        id: u.id,
        name: fullName,
        email: u.email || "—",
        initials: initialsOf(fullName),
        color: colorFor(u.id || fullName),
        totalSeconds: total,
        topFeature: features[0]?.feature || "—",
        features,
      };
    }).sort((a, b) => b.totalSeconds - a.totalSeconds);
    const grandTotal = rows.reduce((a, r) => a + r.totalSeconds, 0);
    return { rows, grandTotal };
  }, [accountUsers]);

  const timeKpis = useMemo(() => {
    const totalSeconds = featureBreakdown.total;
    const featureCount = featureBreakdown.entries.length;
    const contributingUsers = userTimeBreakdown.rows.filter((r) => r.totalSeconds > 0).length;
    const top = featureBreakdown.entries[0];
    return {
      totalSeconds,
      featureCount,
      contributingUsers,
      topFeature: top ? `${top.feature} (${formatDuration(top.seconds)})` : "—",
      avgPerUser: contributingUsers ? Math.round(totalSeconds / contributingUsers) : 0,
    };
  }, [featureBreakdown, userTimeBreakdown]);

  /* Feedback scoped to this sub-account, joined with user records by email */
  const usersByEmail = useMemo(() => {
    const m = new Map();
    users.forEach((u) => { if (u?.email) m.set(String(u.email).toLowerCase(), u); });
    return m;
  }, [users]);

  const accountFeedback = useMemo(() => {
    return feedback
      .filter((f) => !f.locationId || String(f.locationId) === String(accountId))
      .map((f) => {
        const sentiment = sentimentFor(f.emojiSelected);
        const rating = RATING_FROM_SENTIMENT[sentiment] ?? 3;
        const u = f.email ? usersByEmail.get(String(f.email).toLowerCase()) : null;
        const userName = u
          ? (`${u.firstName || ""} ${u.lastName || ""}`.trim() || u.username || u.email || f.email)
          : (f.email || "Anonymous");
        return {
          id: f._id,
          user: userName,
          email: f.email || "—",
          initials: initialsOf(userName),
          color: colorFor(f.email || userName),
          rating,
          sentiment,
          date: formatDateTime(f.createdAt) || "—",
          comment: f.feedback || "",
        };
      });
  }, [feedback, accountId, usersByEmail]);

  const feedbackStats = useMemo(() => {
    const buckets = { "Very Happy": 0, "Happy": 0, "Neutral": 0, "Not Happy": 0 };
    accountFeedback.forEach((f) => { buckets[f.sentiment] = (buckets[f.sentiment] || 0) + 1; });
    const total = accountFeedback.length;
    const sumRating = accountFeedback.reduce((a, f) => a + f.rating, 0);
    const avg = total ? (sumRating / total) : 0;
    return { buckets, total, avg };
  }, [accountFeedback]);

  const trendOptions = {
    chart: { type: "line", toolbar: { show: false }, zoom: { enabled: false } },
    stroke: { curve: "smooth", width: 2 },
    colors: ["#3b82f6"],
    xaxis: {
      categories: trendCats,
      labels: { style: { fontSize: "11px" }, rotate: -30, hideOverlappingLabels: true },
      axisBorder: { show: false }, axisTicks: { show: false },
    },
    yaxis: { min: 0, labels: { style: { fontSize: "11px" } } },
    grid: { borderColor: "#f3f4f6", strokeDashArray: 4 },
    dataLabels: { enabled: false },
    tooltip: { y: { formatter: (v) => trendMetric === "timeSpent" ? `${v}m` : `${v} logins` } },
  };
  const trendSeries = [{ name: trendMetric === "timeSpent" ? "Time Spent" : "Logins", data: trendVals }];

  const tabs = [
    { key: "loginActivity", label: "Login Activity" },
    { key: "timeSpent",     label: "Time Spent" },
    { key: "feedback",      label: "Feedback" },
  ];

  return (
    <div style={{ display: "flex", minHeight: "calc(100vh - 72px)" }}>

      {/* ── Left sidebar ── */}
      <div style={{ width: 270, flexShrink: 0, borderRight: "1px solid var(--border-color)", padding: "24px 20px", background: "var(--base)" }}>
        <p className="text-xs fw-semibold text-secondary-light mb-12" style={{ letterSpacing: "0.08em", textTransform: "uppercase" }}>Account Info</p>
        <h6 className="fw-bold mb-16" style={{ fontSize: 15 }}>{account.name}</h6>

        {/* Owner */}
        <div className="d-flex align-items-center gap-10 mb-16">
          <div className="d-flex align-items-center justify-content-center rounded-circle fw-bold flex-shrink-0"
            style={{ width: 36, height: 36, background: account.color + "22", color: account.color, fontSize: 13 }}>
            {account.initials || account.name[0]}
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 500 }}>{`${accountRaw.firstName || ""} ${accountRaw.lastName || ""}`.trim() || "—"}</div>
            <div style={{ fontSize: 11, color: "#9ca3af" }}>Account Owner</div>
          </div>
        </div>

        {accountRaw.phone && (
          <div className="d-flex align-items-center gap-8 mb-8">
            <Icon icon="solar:phone-outline" style={{ fontSize: 14, color: "#9ca3af" }} />
            <span style={{ fontSize: 13, color: "#374151" }}>{accountRaw.phone}</span>
          </div>
        )}
        {accountRaw.email && (
          <div className="d-flex align-items-center gap-8 mb-20">
            <Icon icon="solar:letter-outline" style={{ fontSize: 14, color: "#9ca3af" }} />
            <span style={{ fontSize: 12, color: "#374151" }}>{accountRaw.email}</span>
          </div>
        )}

        <div style={{ borderTop: "1px solid var(--border-color)", paddingTop: 16, marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>{account.plan}</div>
          {accountRaw.dateAdded && (
            <div className="d-flex align-items-center gap-6 mb-12" style={{ fontSize: 12, color: "#9ca3af" }}>
              <Icon icon="solar:calendar-outline" style={{ fontSize: 13 }} />
              Signed up {formatDateTime(accountRaw.dateAdded)}
            </div>
          )}

          {(() => {
            const since = accountRaw.dateAdded || accountRaw.createdAt;
            const daysActive = since ? Math.max(0, daysBetween(since)) : null;
            const sidebarItems = [
              { icon: "solar:clock-circle-outline",   color: "#3b82f6", bg: "#eff6ff", value: daysActive != null ? `${daysActive} days` : "—",   sub: "Active since signup" },
              { icon: "solar:users-group-rounded-outline", color: "#8b5cf6", bg: "#f5f3ff", value: `${accountUsers.length} Members`, sub: "Team size" },
              { icon: "solar:heart-pulse-outline",    color: "#ef4444", bg: "#fef2f2", value: account.status, sub: "Activity Status", valueColor: account.status === "Active" ? "#16a34a" : "#dc2626" },
            ];
            return sidebarItems.map((item, i) => (
            <div key={i} className="d-flex align-items-center gap-10 mb-10">
              <div className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                style={{ width: 30, height: 30, background: item.bg }}>
                <Icon icon={item.icon} style={{ fontSize: 15, color: item.color }} />
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: item.valueColor || "#374151" }}>{item.value}</div>
                <div style={{ fontSize: 11, color: "#9ca3af" }}>{item.sub}</div>
              </div>
            </div>
            ));
          })()}

          <span style={{ display: "inline-block", padding: "3px 12px", borderRadius: 20, background: "#eff6ff", color: "#2563eb", fontSize: 12, fontWeight: 600, marginTop: 4 }}>
            {account.status}
          </span>
        </div>

      </div>

      {/* ── Main content ── */}
      <div style={{ flex: 1, minWidth: 0, padding: "24px 28px" }}>

        {/* Back */}
        <button
          onClick={onBack}
          className="btn btn-sm radius-8 px-12 py-6 d-inline-flex align-items-center gap-6 mb-12"
          style={{ border: "1px solid var(--border-color)", background: "#fff", fontSize: 13, color: "#374151" }}>
          <Icon icon="solar:alt-arrow-left-outline" style={{ fontSize: 14 }} /> Back
        </button>

        {/* Header */}
        <div className="mb-4">
          <p className="text-xs fw-semibold text-secondary-light mb-4 d-flex align-items-center gap-6" style={{ letterSpacing: "0.08em", textTransform: "uppercase" }}>
            <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#3b82f6", display: "inline-block" }} />
            Account Intelligence
          </p>
          <h4 className="fw-bold mb-4" style={{ letterSpacing: "-0.01em" }}>{account.name}</h4>
          <p className="text-secondary-light text-xs mb-0">Engagement and sentiment insights for this sub-account</p>
        </div>

        {/* Tabs */}
        <div className="d-flex mb-24" style={{ borderBottom: "2px solid var(--border-color)" }}>
          {tabs.map((tab) => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              style={{ padding: "10px 18px", background: "none", border: "none", borderBottom: activeTab === tab.key ? "2px solid var(--primary-600)" : "2px solid transparent", marginBottom: -2, color: activeTab === tab.key ? "var(--primary-600)" : "var(--text-secondary-light)", fontWeight: activeTab === tab.key ? 600 : 400, fontSize: 14, cursor: "pointer", transition: "color 0.15s", whiteSpace: "nowrap" }}>
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === "loginActivity" && (
          <>
            {/* Top stat cards */}
            <div className="row gy-3 mb-20">
              {statCards.map((s, i) => (
                <div className="col-xl-4 col-sm-4" key={i}>
                  <div className="card" style={{ border: "1px solid var(--border-color)" }}>
                    <div className="card-body p-20">
                      <p style={{ fontSize: 11, fontWeight: 600, color: "#9ca3af", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 6 }}>{s.label}</p>
                      <h4 className="fw-bold mb-6" style={{ fontSize: 26, letterSpacing: "-0.01em" }}>{s.value}</h4>
                      <p style={{ fontSize: 12, color: "var(--primary-600)", marginBottom: 0 }}>{s.sub}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Search + Export */}
            <div className="d-flex align-items-center gap-12 mb-16">
              <div className="flex-grow-1 position-relative" style={{ maxWidth: 460 }}>
                <Icon icon="solar:magnifer-outline" style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", fontSize: 16, color: "#9ca3af" }} />
                <input className="form-control form-control-sm radius-8" style={{ paddingLeft: 36 }}
                  placeholder="Search users by name or email"
                  value={userSearch} onChange={(e) => setUserSearch(e.target.value)} />
              </div>
              <button className="btn btn-sm ms-auto d-flex align-items-center gap-6 radius-8 px-14 py-6"
                style={{ border: "1px solid var(--border-color)", background: "#fff", fontSize: 13 }}>
                <Icon icon="solar:export-outline" style={{ fontSize: 14 }} /> Export CSV
              </button>
            </div>

            {/* Users table */}
            <div className="card mb-24" style={{ overflow: "hidden" }}>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ background: "#fafafa", borderBottom: "1px solid var(--border-color)" }}>
                      <th style={{ width: 36, padding: "10px 16px" }}><input type="checkbox" style={{ accentColor: "var(--primary-600)" }} /></th>
                      {["User","Role","Last Login","Time Spent","Login Frequency"].map((label) => (
                        <th key={label} style={{ padding: "10px 12px", fontSize: 11, fontWeight: 600, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.04em", whiteSpace: "nowrap" }}>
                          {label}
                        </th>
                      ))}
                    </tr>
                    <tr style={{ borderBottom: "1px solid var(--border-color)", background: "#fff" }}>
                      <td colSpan={6} style={{ padding: "8px 16px" }}>
                        <span style={{ fontWeight: 600, fontSize: 13 }}>Users</span>
                        <span style={{ marginLeft: 8, background: "#f3f4f6", borderRadius: 12, padding: "2px 8px", fontSize: 12, fontWeight: 600 }}>{filteredUserRows.length}</span>
                      </td>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUserRows.length === 0 && (
                      <tr><td colSpan={6} className="text-center py-32 text-secondary-light text-sm">No users for this sub-account</td></tr>
                    )}
                    {filteredUserRows.map((u, i) => {
                      const fs = FREQ_STYLE[u.freq] || FREQ_STYLE["Ghosting"];
                      const isAdmin = /admin/i.test(u.role);
                      const roleStyle = isAdmin
                        ? { bg: "#f0fdf4", color: "#15803d" }
                        : { bg: "#eff6ff", color: "#2563eb" };
                      return (
                        <tr key={u.id || i} style={{ borderBottom: "1px solid var(--border-color)" }}>
                          <td style={{ padding: "12px 16px" }}><input type="checkbox" style={{ accentColor: "var(--primary-600)" }} /></td>
                          <td style={{ padding: "12px 12px" }}>
                            <div className="d-flex align-items-center gap-10">
                              <div className="d-flex align-items-center justify-content-center rounded-circle fw-bold"
                                style={{ width: 34, height: 34, background: u.color + "22", color: u.color, fontSize: 12, flexShrink: 0 }}>{u.initials}</div>
                              <div>
                                <div style={{ fontSize: 13, fontWeight: 500 }}>{u.name}</div>
                                <div style={{ fontSize: 11, color: "#9ca3af" }}>{u.email}</div>
                              </div>
                            </div>
                          </td>
                          <td style={{ padding: "12px 12px" }}>
                            <span style={{ padding: "3px 10px", borderRadius: 20, background: roleStyle.bg, color: roleStyle.color, fontSize: 12, fontWeight: 600 }}>{u.role}</span>
                          </td>
                          <td style={{ padding: "12px 12px", fontSize: 13, color: u.lastLogin ? "#374151" : "#9ca3af" }}>{u.lastLogin || "Never"}</td>
                          <td style={{ padding: "12px 12px", fontSize: 13, color: u.timeSpent ? "#374151" : "#9ca3af" }}>{u.timeSpent || "-"}</td>
                          <td style={{ padding: "12px 12px" }}>
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 10px", borderRadius: 20, background: fs.bg, color: fs.color, fontSize: 12, fontWeight: 600 }}>
                              <span style={{ width: 7, height: 7, borderRadius: "50%", background: fs.dot, display: "inline-block" }} />{u.freq}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Login Trend chart */}
            <div className="card">
              <div className="card-body p-24">
                <div className="d-flex align-items-start justify-content-between flex-wrap gap-12 mb-20">
                  <div>
                    <h6 className="fw-semibold mb-2">Login Trend</h6>
                    <p className="text-secondary-light text-xs mb-0">
                      {trendMetric === "timeSpent" ? "Time spent (minutes) over time" : "Number of logins over time"}
                    </p>
                  </div>
                  <div className="d-flex align-items-center gap-8">
                    <div className="d-flex" style={{ border: "1px solid var(--border-color)", borderRadius: 8, overflow: "hidden" }}>
                      {["timeSpent", "logins"].map((m) => (
                        <button key={m} onClick={() => setTrendMetric(m)}
                          style={{ padding: "5px 14px", border: "none", background: trendMetric === m ? "var(--primary-600)" : "transparent", color: trendMetric === m ? "#fff" : "var(--text-secondary-light)", fontSize: 13, fontWeight: trendMetric === m ? 600 : 400, cursor: "pointer" }}>
                          {m === "timeSpent" ? "Time Spent" : "Logins"}
                        </button>
                      ))}
                    </div>
                    <div className="d-flex" style={{ border: "1px solid var(--border-color)", borderRadius: 8, overflow: "hidden" }}>
                      {["7D", "30D", "90D"].map((r) => (
                        <button key={r} onClick={() => setTrendRange(r)}
                          style={{ padding: "5px 12px", border: "none", background: trendRange === r ? "#1f2937" : "transparent", color: trendRange === r ? "#fff" : "var(--text-secondary-light)", fontSize: 13, fontWeight: trendRange === r ? 600 : 400, cursor: "pointer" }}>
                          {r}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                <ReactApexChart options={trendOptions} series={trendSeries} type="line" height={220} />
              </div>
            </div>
          </>
        )}

        {activeTab === "timeSpent" && (
          <>
            {/* KPI cards */}
            <div className="row gy-3 mb-20">
              {[
                { label: "TOTAL TIME SPENT",   value: formatDuration(timeKpis.totalSeconds),  sub: "Across all features" },
                { label: "FEATURES USED",       value: String(timeKpis.featureCount),          sub: "Distinct features" },
                { label: "CONTRIBUTING USERS",  value: `${timeKpis.contributingUsers} / ${accountUsers.length}`, sub: "Users with logged time" },
                { label: "AVG TIME PER USER",   value: formatDuration(timeKpis.avgPerUser),    sub: "Among contributors" },
              ].map((s, i) => (
                <div className="col-xl-3 col-sm-6" key={i}>
                  <div className="card h-100" style={{ border: "1px solid var(--border-color)" }}>
                    <div className="card-body p-20">
                      <p style={{ fontSize: 11, fontWeight: 600, color: "#9ca3af", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 6 }}>{s.label}</p>
                      <h4 className="fw-bold mb-6" style={{ fontSize: 24, letterSpacing: "-0.01em" }}>{s.value}</h4>
                      <p style={{ fontSize: 12, color: "var(--primary-600)", marginBottom: 0 }}>{s.sub}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Top feature highlight */}
            <div className="card mb-20" style={{ border: "1px solid var(--border-color)" }}>
              <div className="card-body p-20 d-flex align-items-center justify-content-between gap-12">
                <div className="d-flex align-items-center gap-12">
                  <div className="d-flex align-items-center justify-content-center rounded-circle"
                    style={{ width: 44, height: 44, background: "#eff6ff" }}>
                    <Icon icon="solar:medal-star-bold-duotone" style={{ fontSize: 22, color: "#2563eb" }} />
                  </div>
                  <div>
                    <p className="text-xs text-secondary-light mb-2" style={{ letterSpacing: "0.08em", textTransform: "uppercase", fontWeight: 600 }}>Top Feature</p>
                    <h6 className="fw-bold mb-0">{timeKpis.topFeature}</h6>
                  </div>
                </div>
              </div>
            </div>

            {/* Time per Feature */}
            <div className="card mb-24" style={{ overflow: "hidden" }}>
              <div className="card-header border-bottom bg-base py-16 px-24">
                <h6 className="fw-semibold mb-2">Time Spent per Feature</h6>
                <p className="text-secondary-light text-xs mb-0">Aggregated across all users in this sub-account</p>
              </div>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 560 }}>
                  <thead>
                    <tr style={{ background: "#fafafa", borderBottom: "1px solid var(--border-color)" }}>
                      {["Feature", "Time", "Share", "Distribution"].map((label, i) => (
                        <th key={label} style={{ padding: "10px 16px", fontSize: 11, fontWeight: 600, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.05em", textAlign: i === 1 || i === 2 ? "right" : "left", whiteSpace: "nowrap" }}>{label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {featureBreakdown.entries.length === 0 && (
                      <tr><td colSpan={4} className="text-center py-32 text-secondary-light text-sm">No time tracked yet for this sub-account</td></tr>
                    )}
                    {featureBreakdown.entries.map((e, i) => {
                      const pct = featureBreakdown.total > 0 ? (e.seconds / featureBreakdown.total) * 100 : 0;
                      const barColor = i === 0 ? "#2563eb" : i === 1 ? "#3b82f6" : i === 2 ? "#60a5fa" : "#93c5fd";
                      return (
                        <tr key={e.feature} style={{ borderBottom: i < featureBreakdown.entries.length - 1 ? "1px solid var(--border-color)" : "none" }}>
                          <td style={{ padding: "12px 16px" }}>
                            <div className="d-flex align-items-center gap-10">
                              <span style={{ width: 8, height: 8, borderRadius: "50%", background: barColor, display: "inline-block", flexShrink: 0 }} />
                              <span style={{ fontSize: 13, fontWeight: 500, textTransform: "capitalize" }}>{e.feature}</span>
                            </div>
                          </td>
                          <td style={{ padding: "12px 16px", textAlign: "right", fontSize: 13, fontWeight: 600, whiteSpace: "nowrap" }}>{formatDuration(e.seconds)}</td>
                          <td style={{ padding: "12px 16px", textAlign: "right", fontSize: 13, color: "#6b7280" }}>{pct.toFixed(1)}%</td>
                          <td style={{ padding: "12px 16px", minWidth: 200 }}>
                            <div style={{ height: 8, borderRadius: 4, background: "#f3f4f6", overflow: "hidden" }}>
                              <div style={{ width: `${pct}%`, height: "100%", background: barColor, transition: "width 0.3s" }} />
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  {featureBreakdown.entries.length > 0 && (
                    <tfoot>
                      <tr style={{ background: "#fafafa", borderTop: "2px solid var(--border-color)" }}>
                        <td style={{ padding: "12px 16px", fontSize: 12, fontWeight: 700, color: "#374151" }}>Total</td>
                        <td style={{ padding: "12px 16px", textAlign: "right", fontSize: 13, fontWeight: 700 }}>{formatDuration(featureBreakdown.total)}</td>
                        <td style={{ padding: "12px 16px", textAlign: "right", fontSize: 13, fontWeight: 700 }}>100%</td>
                        <td />
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </div>

            {/* Time per User */}
            <div className="card" style={{ overflow: "hidden" }}>
              <div className="card-header border-bottom bg-base py-16 px-24">
                <h6 className="fw-semibold mb-2">Time Spent per User</h6>
                <p className="text-secondary-light text-xs mb-0">Sum of each user's recorded time across features</p>
              </div>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 640 }}>
                  <thead>
                    <tr style={{ background: "#fafafa", borderBottom: "1px solid var(--border-color)" }}>
                      {["User", "Top Feature", "Time", "Share", "Distribution"].map((label, i) => (
                        <th key={label} style={{ padding: "10px 16px", fontSize: 11, fontWeight: 600, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.05em", textAlign: i === 2 || i === 3 ? "right" : "left", whiteSpace: "nowrap" }}>{label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {userTimeBreakdown.rows.length === 0 && (
                      <tr><td colSpan={5} className="text-center py-32 text-secondary-light text-sm">No users in this sub-account</td></tr>
                    )}
                    {userTimeBreakdown.rows.map((u, i) => {
                      const pct = userTimeBreakdown.grandTotal > 0 ? (u.totalSeconds / userTimeBreakdown.grandTotal) * 100 : 0;
                      return (
                        <tr key={u.id || i} style={{ borderBottom: i < userTimeBreakdown.rows.length - 1 ? "1px solid var(--border-color)" : "none" }}>
                          <td style={{ padding: "12px 16px", minWidth: 200 }}>
                            <div className="d-flex align-items-center gap-10">
                              <div className="d-flex align-items-center justify-content-center rounded-circle fw-bold flex-shrink-0"
                                style={{ width: 32, height: 32, background: u.color + "22", color: u.color, fontSize: 12 }}>{u.initials}</div>
                              <div>
                                <div style={{ fontSize: 13, fontWeight: 500 }}>{u.name}</div>
                                <div style={{ fontSize: 11, color: "#9ca3af" }}>{u.email}</div>
                              </div>
                            </div>
                          </td>
                          <td style={{ padding: "12px 16px", fontSize: 13, color: "#374151", textTransform: "capitalize" }}>{u.topFeature}</td>
                          <td style={{ padding: "12px 16px", textAlign: "right", fontSize: 13, fontWeight: 600, whiteSpace: "nowrap" }}>{formatDuration(u.totalSeconds)}</td>
                          <td style={{ padding: "12px 16px", textAlign: "right", fontSize: 13, color: "#6b7280" }}>{pct.toFixed(1)}%</td>
                          <td style={{ padding: "12px 16px", minWidth: 200 }}>
                            <div style={{ height: 8, borderRadius: 4, background: "#f3f4f6", overflow: "hidden" }}>
                              <div style={{ width: `${pct}%`, height: "100%", background: u.color, transition: "width 0.3s" }} />
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  {userTimeBreakdown.grandTotal > 0 && (
                    <tfoot>
                      <tr style={{ background: "#fafafa", borderTop: "2px solid var(--border-color)" }}>
                        <td style={{ padding: "12px 16px", fontSize: 12, fontWeight: 700, color: "#374151" }}>Total</td>
                        <td />
                        <td style={{ padding: "12px 16px", textAlign: "right", fontSize: 13, fontWeight: 700 }}>{formatDuration(userTimeBreakdown.grandTotal)}</td>
                        <td style={{ padding: "12px 16px", textAlign: "right", fontSize: 13, fontWeight: 700 }}>100%</td>
                        <td />
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </div>
          </>
        )}

        {activeTab === "feedback" && (
          <>
            {/* Rating Distribution */}
            <div className="card mb-20">
              <div className="card-body p-24">
                <div className="d-flex align-items-start justify-content-between mb-20">
                  <div>
                    <h6 className="fw-semibold mb-2">Rating Distribution</h6>
                    <p className="text-secondary-light text-xs mb-0">Latest customer sentiment for this sub-account</p>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <p style={{ fontSize: 10, fontWeight: 600, color: "#9ca3af", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 2 }}>Average Rating</p>
                    <p style={{ fontSize: 20, fontWeight: 700, marginBottom: 0 }}>{feedbackStats.avg.toFixed(1)} / 5.0</p>
                  </div>
                </div>

                <div className="d-flex align-items-center gap-32 flex-wrap">
                  {/* Donut */}
                  {(() => {
                    const r = 46;
                    const C = 2 * Math.PI * r;
                    const happyPct = feedbackStats.total
                      ? ((feedbackStats.buckets["Very Happy"] + feedbackStats.buckets["Happy"]) / feedbackStats.total)
                      : 0;
                    const stroke = feedbackStats.avg >= 4 ? "#22c55e" : feedbackStats.avg >= 3 ? "#f59e0b" : "#ef4444";
                    const textColor = feedbackStats.avg >= 4 ? "#16a34a" : feedbackStats.avg >= 3 ? "#d97706" : "#ef4444";
                    return (
                      <div style={{ flexShrink: 0 }}>
                        <svg width={120} height={120} viewBox="0 0 120 120">
                          <circle cx={60} cy={60} r={r} fill="none" stroke="#f3f4f6" strokeWidth={12} />
                          <circle cx={60} cy={60} r={r} fill="none" stroke={stroke} strokeWidth={12}
                            strokeDasharray={`${C * happyPct} ${C}`}
                            strokeLinecap="round" transform="rotate(-90 60 60)" />
                          <text x={60} y={55} textAnchor="middle" style={{ fontSize: 20, fontWeight: 700, fill: textColor }}>{feedbackStats.avg.toFixed(1)}</text>
                          <text x={60} y={70} textAnchor="middle" style={{ fontSize: 10, fill: "#9ca3af" }}>OUT OF 5.0</text>
                        </svg>
                      </div>
                    );
                  })()}

                  {/* Rows */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {[
                      { label: "Very Happy", dot: "#22c55e", stars: 5,  filledColor: "#22c55e" },
                      { label: "Happy",      dot: "#3b82f6", stars: 4,  filledColor: "#3b82f6" },
                      { label: "Neutral",    dot: "#f59e0b", stars: 3,  filledColor: "#f59e0b" },
                      { label: "Not Happy",  dot: "#ef4444", stars: 1,  filledColor: "#ef4444" },
                    ].map((row, i) => {
                      const count = feedbackStats.buckets[row.label] || 0;
                      const pct = feedbackStats.total ? Math.round((count / feedbackStats.total) * 100) : 0;
                      return (
                        <div key={i} className="d-flex align-items-center gap-12"
                          style={{ padding: "10px 0", borderBottom: i < 3 ? "1px solid var(--border-color)" : "none" }}>
                          <div className="d-flex align-items-center gap-8" style={{ minWidth: 110 }}>
                            <span style={{ width: 10, height: 10, borderRadius: "50%", background: row.dot, display: "inline-block", flexShrink: 0 }} />
                            <span style={{ fontSize: 13 }}>{row.label}</span>
                          </div>
                          <div style={{ flex: 1, height: 6, borderRadius: 3, background: "#f3f4f6", overflow: "hidden" }}>
                            <div style={{ width: `${pct}%`, height: "100%", background: row.filledColor, transition: "width 0.3s" }} />
                          </div>
                          <span style={{ fontSize: 12, color: "#9ca3af", minWidth: 110, textAlign: "right" }}>{count} responses&nbsp; {pct}%</span>
                          <div className="d-flex gap-2" style={{ minWidth: 90, justifyContent: "flex-end" }}>
                            {[1,2,3,4,5].map((s) => (
                              <Icon key={s}
                                icon={s <= row.stars ? "solar:star-bold" : "solar:star-outline"}
                                style={{ fontSize: 14, color: s <= row.stars ? row.filledColor : "#d1d5db" }} />
                            ))}
                          </div>
                        </div>
                      );
                    })}
                    <p style={{ fontSize: 12, color: "#9ca3af", marginTop: 12, marginBottom: 0 }}>{feedbackStats.total} total responses</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Feedback */}
            {(() => {
              const feedbackRows = accountFeedback;
              const sentimentStyle = {
                "Very Happy": { bg: "#f0fdf4", color: "#16a34a" },
                "Happy":      { bg: "#eff6ff", color: "#2563eb" },
                "Neutral":    { bg: "#fef9c3", color: "#92400e" },
                "Not Happy":  { bg: "#fef2f2", color: "#dc2626" },
              };
              const starColor = { "Very Happy": "#22c55e", "Happy": "#3b82f6", "Neutral": "#f59e0b", "Not Happy": "#ef4444" };
              return (
                <div className="card">
                  <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center justify-content-between">
                    <div>
                      <h6 className="fw-semibold mb-2">Recent Feedback</h6>
                      <p className="text-secondary-light text-xs mb-0">Showing {feedbackRows.length} of {feedbackRows.length} responses</p>
                    </div>
                    <button className="btn btn-sm radius-8 px-14 py-6 d-flex align-items-center gap-6"
                      style={{ border: "1px solid var(--border-color)", background: "#fff", fontSize: 13 }}>
                      <Icon icon="solar:export-outline" style={{ fontSize: 14 }} /> Export
                    </button>
                  </div>
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 620 }}>
                      <thead>
                        <tr style={{ background: "#fafafa", borderBottom: "1px solid var(--border-color)" }}>
                          {["User", "Rating", "Sentiment", "Date", "Comment"].map((col, ci) => (
                            <th key={ci} style={{ padding: "10px 16px", fontSize: 11, fontWeight: 600, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.05em", whiteSpace: "nowrap" }}>
                              {col}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {feedbackRows.length === 0 && (
                          <tr><td colSpan={5} className="text-center py-32 text-secondary-light text-sm">No feedback yet for this sub-account</td></tr>
                        )}
                        {feedbackRows.map((row, i) => {
                          const ss = sentimentStyle[row.sentiment];
                          const sc = starColor[row.sentiment];
                          return (
                            <tr key={i}
                              style={{ borderBottom: i < feedbackRows.length - 1 ? "1px solid var(--border-color)" : "none", transition: "background 0.12s" }}
                              onMouseEnter={(e) => e.currentTarget.style.background = "#fafbff"}
                              onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                            >
                              {/* User */}
                              <td style={{ padding: "14px 16px", minWidth: 180 }}>
                                <div className="d-flex align-items-center gap-10">
                                  <div className="d-flex align-items-center justify-content-center rounded-circle fw-bold flex-shrink-0"
                                    style={{ width: 32, height: 32, background: row.color + "22", color: row.color, fontSize: 12 }}>
                                    {row.initials}
                                  </div>
                                  <div>
                                    <div style={{ fontSize: 13, fontWeight: 500 }}>{row.user}</div>
                                    <div style={{ fontSize: 11, color: "#9ca3af" }}>{row.email}</div>
                                  </div>
                                </div>
                              </td>
                              {/* Rating stars */}
                              <td style={{ padding: "14px 16px", whiteSpace: "nowrap" }}>
                                <div className="d-flex align-items-center gap-2">
                                  {[1,2,3,4,5].map((s) => (
                                    <Icon key={s}
                                      icon={s <= row.rating ? "solar:star-bold" : "solar:star-outline"}
                                      style={{ fontSize: 14, color: s <= row.rating ? sc : "#d1d5db" }} />
                                  ))}
                                </div>
                              </td>
                              {/* Sentiment badge */}
                              <td style={{ padding: "14px 16px" }}>
                                <span style={{ fontSize: 12, fontWeight: 600, padding: "3px 10px", borderRadius: 20, background: ss.bg, color: ss.color, whiteSpace: "nowrap" }}>
                                  {row.sentiment}
                                </span>
                              </td>
                              {/* Date */}
                              <td style={{ padding: "14px 16px", fontSize: 12, color: "#6b7280", whiteSpace: "nowrap" }}>
                                {row.date}
                              </td>
                              {/* Comment */}
                              <td style={{ padding: "14px 16px", fontSize: 13, color: "#374151", maxWidth: 320 }}>
                                <span style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                                  {row.comment}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })()}
          </>
        )}
      </div>
    </div>
  );
};

export default SubAccountDetail;
